import JSZip from 'jszip';
import { ToneAndChildren, sanitizeFilename, uniqueZipEntryName } from './helpers';
import { saveAs } from 'file-saver';
import { type ArchitectureVersion } from './constants';
import { modelFileUrl, pageShareToken } from '@/utils/models/file-url';

interface DownloadToneModelsProps {
  tone: ToneAndChildren;
  onSuccess?: () => Promise<void>;
  isOAuthFlow?: boolean;
  authorizationId?: string | null;
  /**
   * Filter the zipped model set by architecture. Only applies to the default
   * (core-site) file-bundling branch below; the OAuth flow hands off the
   * tone_id and doesn't read this.
   */
  architectureFilter?: ArchitectureVersion;
}

// check if the user is on iOS
export function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

export async function downloadToneModels({
  tone,
  onSuccess,
  isOAuthFlow,
  authorizationId,
  architectureFilter,
}: DownloadToneModelsProps) {
  if (!tone.models?.length) return;

  const zip = new JSZip();
  const usedNames = new Set<string>();

  try {
    // OAuth select flow: hand the tone off and bounce to the partner callback
    if (isOAuthFlow) {
      if (!authorizationId) {
        throw new Error('OAuth flow missing authorization_id');
      }
      const response = await fetch('/api/v1/select/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tone_id: tone.id, authorization_id: authorizationId }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error_description ?? 'Failed to complete selection');
      }
      const { redirect_url } = await response.json();
      console.log('[t3k_oauth] client: pre-onSuccess');
      const tOnSuccess = Date.now();
      if (onSuccess) await onSuccess();
      console.log('[t3k_oauth] client: post-onSuccess', { elapsed_ms: Date.now() - tOnSuccess });
      console.log('[t3k_oauth] client: assigning window.location.href', {
        redirect_host: new URL(redirect_url).host,
      });
      window.location.href = redirect_url;
      return;
    }

    // Download all models (optionally filtered by architecture).
    const modelsToZip = architectureFilter
      ? tone.models.filter((m) => m.architecture_version === architectureFilter)
      : tone.models;
    const modelDownloads = await Promise.all(
      modelsToZip.map(async (model) => {
        if (!model.model_url || model.id === undefined) return null;

        const response = await fetch(modelFileUrl({ id: model.id, model_url: model.model_url }, { token: pageShareToken() }));
        if (!response.ok) {
          console.error('Failed to download model:', model.name);
          return null;
        }

        const data = await response.arrayBuffer();
        const fileName = uniqueZipEntryName(model.name, model.model_url, usedNames);
        return { fileName, data };
      })
    );

    // Add successfully downloaded models to zip
    modelDownloads.forEach((model) => {
      if (model) {
        zip.file(model.fileName, model.data);
      }
    });

    // Generate and download zip file
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const filename = `${sanitizeFilename(tone.title || 'tone')}.zip`;
    if (isIOS()) {
      // const reader = new FileReader();
      // reader.onload = function () {
      //   const a = document.createElement('a');
      //   a.href = reader.result as string;
      //   a.download = filename;
      //   a.click();
      // };
      // reader.readAsDataURL(zipBlob);
      saveAs(zipBlob, filename);
    } else {
      // const url = window.URL.createObjectURL(zipBlob);
      // const a = document.createElement('a');
      // a.href = url;
      // a.download = filename;
      // a.click();
      // window.URL.revokeObjectURL(url);
      saveAs(zipBlob, filename);
    }

    if (onSuccess) {
      await onSuccess();
    }
  } catch (error) {
    throw new Error(
      `Failed to create zip file: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}
